// src/pages/Profile.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  AlertCircle,
  Camera,
  Upload,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Edit3,
  Save,
  Shield,
  Calendar,
  CreditCard,
  Package,
  FileText,
  MessageSquare,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import reservationService from "../services/reservationService";
import {
  ModernInput,
  FormSection
} from "../components/ui/FormComponents";

/* =========================
   API BASE (no hardcoded URLs)
========================= */
const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  "https://nouzhaback-theta.vercel.app";

const Profile = () => {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState({ type: "", text: "" });

  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [userInfo, setUserInfo] = useState({
    username: "",
    email: "",
    phone: "",
    address: "",
    bio: "",
  });

  const [reservations, setReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(true);
  const [reservationsError, setReservationsError] = useState(null);

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initialiser les données utilisateur
  useEffect(() => {
    if (!isMounted.current) return;

    const fetchProfile = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/auth/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) return;

      const json = await res.json();
      const p = json.data;

      setUserInfo({
        username: p.username || "",
        email: p.email || "",
        phone: p.phone || "",
        address: p.address || "",
        bio: p.bio || "",
      });
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    if (!isMounted.current) return;

    if (!currentUser) return;
    setUserInfo((prev) => ({
      ...prev,
      username: currentUser.username || prev.username,
      email: currentUser.email || prev.email,
    }));
  }, [currentUser]);

  // Récupérer les réservations de l'utilisateur
  useEffect(() => {
    if (!isMounted.current) return;

    const fetchReservations = async () => {
      if (!currentUser) return;

      try {
        setLoadingReservations(true);
        setReservationsError(null);
        const result = await reservationService.getAllUserReservations();

        if (result.success) {
          setReservations(result.data);
        } else {
          setReservationsError(result.error || "Erreur lors du chargement des réservations");
        }
      } catch (error) {
        console.error("Error fetching reservations:", error);
        setReservationsError("Erreur lors du chargement des réservations");
      } finally {
        setLoadingReservations(false);
      }
    };

    fetchReservations();
  }, [currentUser]);

  // Gestion de l'upload d'image
  const handleImageUpload = async () => {
    if (!selectedImage) {
      alert("Veuillez sélectionner une image");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("profileImage", selectedImage);

      const token = localStorage.getItem("authToken");

      // ✅ plus d'URL en dur
      const response = await fetch(`${API_BASE}/api/auth/profile-image`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Erreur lors de l'upload");
      }

      setMessage({
        type: "success",
        text: "Image de profil mise à jour avec succès!",
      });

      setShowImageModal(false);
      setSelectedImage(null);
      setImagePreview(null);
    } catch (error) {
      setMessage({
        type: "error",
        text: "Erreur lors de la mise à jour de l'image: " + (error?.message || "Erreur inconnue"),
      });
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader(); // Keep this line for reader to be defined
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Gestion de l'édition des informations utilisateur
  const handleEditToggle = () => {
    if (isEditing) {
      handleSaveChanges();
    } else {
      setIsEditing(true);
    }
  };

  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem("authToken");

      // ✅ plus d'URL en dur
      const response = await fetch(`${API_BASE}/api/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(userInfo),
      });

      const json = await response.json();
      if (!response.ok) throw new Error(json.message || "Erreur lors de la mise à jour");

      // ✅ important: récupérer les champs sauvegardés
      const u = json.data;
      setUserInfo({
        username: u.username || "",
        email: u.email || "",
        phone: u.phone || "",
        address: u.address || "",
        bio: u.bio || "",
      });

      setMessage({ type: "success", text: "Informations mises à jour avec succès!" });
      setIsEditing(false);
    } catch (error) {
      setMessage({ type: "error", text: "Erreur lors de la mise à jour: " + error.message });
    }
  };

  const handleInputChange = (field, value) => {
    setUserInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        staggerChildren: 0.1,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF7ED] via-orange-50 to-orange-100 py-12 px-4 sm:px-6 lg:px-8 pt-28">
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="max-w-5xl mx-auto">
        {/* Message de notification */}
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-8 p-4 rounded-2xl flex items-center gap-3 shadow-lg backdrop-blur-sm ${message.type === "success"
                ? "bg-emerald-50/90 border border-emerald-200 text-emerald-700"
                : "bg-red-50/90 border border-red-200 text-red-700"
              }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="h-5 w-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
            )}
            <span className="font-medium">{message.text}</span>
          </motion.div>
        )}

        {/* Main Profile Card */}
        <motion.div
          variants={cardVariants}
          className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-amber-100/50 overflow-hidden"
        >
          {/* Premium Header */}
          <div className="bg-gradient-to-br from-[#d97706] via-orange-600 to-red-600 px-8 py-16 relative">
            <div className="absolute inset-0 bg-black/5"></div>
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-5"></div>

            <div className="relative flex flex-col lg:flex-row items-center gap-10">
              {/* Profile Image - Extra Premium */}
              <div className="relative">
                <div className="w-40 h-40 bg-white rounded-3xl p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.2)] transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                  {currentUser?.profileImage ? (
                    <img
                      src={`${API_BASE}${currentUser.profileImage}`}
                      alt="Profile"
                      className="w-full h-full rounded-2xl object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                        if (e.target.nextSibling && e.target.nextSibling.style) {
                          e.target.nextSibling.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}

                  <div
                    className={`w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center text-white text-5xl font-black ${currentUser?.profileImage ? "hidden" : ""
                      }`}
                  >
                    {currentUser?.username ? currentUser.username.charAt(0).toUpperCase() : "U"}
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1, rotate: 10 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowImageModal(true)}
                  className="absolute -bottom-3 -right-3 p-4 bg-white text-orange-600 rounded-2xl shadow-xl hover:bg-orange-50 transition-all duration-300 border border-orange-100"
                >
                  <Camera className="h-5 w-5" />
                </motion.button>
              </div>

              {/* User Info */}
              <div className="flex-1 text-center lg:text-left">
                <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-6">
                  <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter drop-shadow-lg">
                    {currentUser?.username}
                  </h1>

                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleEditToggle}
                    className={`inline-flex items-center gap-3 px-6 py-3 rounded-2xl font-black uppercase tracking-widest text-xs transition-all duration-300 shadow-xl ${isEditing
                        ? "bg-white text-orange-600"
                        : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-md border border-white/30"
                      }`}
                  >
                    {isEditing ? (
                      <>
                        <Save className="h-4 w-4" />
                        Sauvegarder
                      </>
                    ) : (
                      <>
                        <Edit3 className="h-4 w-4" />
                        Modifier
                      </>
                    )}
                  </motion.button>
                </div>

                <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-2xl text-white font-black text-[10px] uppercase tracking-[0.2em] border border-white/20 shadow-lg">
                  <Shield className="h-4 w-4 text-orange-300" />
                  Compte vérifié
                </div>
              </div>
            </div>
          </div>

          {/* Form Fields */}
          <div className="p-10">
            <form className="space-y-12" onSubmit={(e) => e.preventDefault()}>
              <FormSection title="Coordonnées & Informations" icon={User}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <ModernInput
                    label="Nom d'utilisateur"
                    icon={User}
                    value={userInfo.username}
                    onChange={(e) => handleInputChange("username", e.target.value)}
                    placeholder="Votre nom d'utilisateur"
                    readOnly={!isEditing}
                  />

                  <ModernInput
                    label="Adresse email"
                    icon={Mail}
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="votre.email@exemple.com"
                    readOnly={!isEditing}
                  />

                  <ModernInput
                    label="Numéro de téléphone"
                    icon={Phone}
                    type="tel"
                    value={userInfo.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="+212 6 XX XX XX XX"
                    readOnly={!isEditing}
                  />

                  <ModernInput
                    label="Adresse"
                    icon={MapPin}
                    value={userInfo.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    placeholder="Votre adresse complète"
                    readOnly={!isEditing}
                  />

                  <div className="lg:col-span-2">
                    <ModernInput
                      label="Biographie"
                      icon={MessageSquare}
                      value={userInfo.bio}
                      onChange={(e) => handleInputChange("bio", e.target.value)}
                      multiline
                      rows={4}
                      placeholder="Parlez-nous de vous..."
                      readOnly={!isEditing}
                    />
                  </div>
                </div>
              </FormSection>
            </form>

            {/* Section Réservations */}
            <div className="mt-16">
              <div className="bg-gradient-to-br from-orange-50/80 to-red-50/80 rounded-[3rem] p-10 border border-orange-100 shadow-inner">
                <div className="flex items-center gap-4 mb-10">
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                    <Package className="h-7 w-7 text-orange-600" />
                  </div>
                  <h3 className="text-3xl font-black text-gray-900 tracking-tighter">Mes Réservations</h3>
                </div>

                {loadingReservations ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
                    <span className="ml-4 text-amber-700 font-medium">Chargement des réservations...</span>
                  </div>
                ) : reservationsError ? (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
                    <p className="text-red-700 font-medium">{reservationsError}</p>
                  </div>
                ) : reservations.length === 0 ? (
                  <div className="bg-white/60 rounded-xl p-8 text-center">
                    <Package className="h-16 w-16 text-amber-300 mx-auto mb-4" />
                    <p className="text-amber-700 font-medium text-lg">Aucune réservation trouvée</p>
                    <p className="text-amber-600/70 mt-2">Vos futures réservations apparaîtront ici</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reservations.map((reservation, index) => (
                      <motion.div
                        key={reservation._id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-amber-100 hover:border-amber-200 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="text-2xl">{reservationService.getServiceIcon(reservation.type)}</span>
                              <div>
                                <h4 className="font-bold text-amber-900 text-lg">{reservation.serviceName}</h4>
                                <p className="text-amber-700/70 text-sm capitalize">{reservation.type}</p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-amber-600" />
                                <span className="text-amber-800 font-medium">
                                  {new Date(reservation.date).toLocaleDateString("fr-FR", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  })}
                                </span>
                              </div>

                              {reservation.price && (
                                <div className="flex items-center gap-2">
                                  <CreditCard className="h-4 w-4 text-amber-600" />
                                  <span className="text-amber-800 font-medium">
                                    {reservation.price.toLocaleString("fr-FR")} DH
                                  </span>
                                </div>
                              )}

                              {reservation.reference && (
                                <div className="flex items-center gap-2">
                                  <span className="text-amber-600 text-sm font-medium">Réf:</span>
                                  <span className="text-amber-800 font-mono text-sm">{reservation.reference}</span>
                                </div>
                              )}
                            </div>

                            {/* Bouton pour demander une facture */}
                            <div className="mt-4">
                              <Link
                                to={`/invoice-request/${reservation._id}/${reservation.type}`}
                                className="inline-flex items-center gap-2 text-sm px-4 py-2 bg-amber-100 text-amber-800 hover:bg-amber-200 rounded-lg transition-colors"
                              >
                                <FileText className="h-4 w-4" />
                                Demander une facture
                              </Link>
                            </div>
                          </div>

                          <div className="ml-4">
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${reservationService.getStatusColor(
                                reservation.status
                              )}`}
                            >
                              {reservationService.getStatusText(reservation.status)}
                            </span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Modal pour l'upload d'image */}
        <AnimatePresence>
          {showImageModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 pt-20"
              onClick={() => setShowImageModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl border border-amber-100 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl mb-4">
                    <Camera className="h-8 w-8 text-amber-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-amber-900 mb-2">Changer la photo de profil</h3>
                  <p className="text-amber-700/70">Sélectionnez une nouvelle image pour votre profil</p>

                  <button
                    onClick={() => setShowImageModal(false)}
                    className="absolute top-6 right-6 p-2 hover:bg-amber-50 rounded-full transition-colors"
                  >
                    <X className="h-6 w-6 text-amber-600" />
                  </button>
                </div>

                <div className="space-y-6">
                  {imagePreview && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center"
                    >
                      <div className="relative inline-block">
                        <img
                          src={imagePreview}
                          alt="Aperçu"
                          className="w-40 h-40 rounded-2xl object-cover mx-auto border-4 border-amber-200 shadow-lg"
                        />
                        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-white flex items-center justify-center">
                          <CheckCircle className="h-4 w-4 text-white" />
                        </div>
                      </div>
                      <p className="text-amber-700 mt-4 font-medium">Aperçu de votre nouvelle photo</p>
                    </motion.div>
                  )}

                  <div className="border-2 border-dashed border-amber-300 rounded-2xl p-8 text-center bg-gradient-to-br from-amber-50/50 to-orange-50/50 hover:from-amber-100/50 hover:to-orange-100/50 transition-all duration-200">
                    <div className="bg-amber-100 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Upload className="h-10 w-10 text-amber-600" />
                    </div>
                    <label className="cursor-pointer block">
                      <span className="text-amber-600 hover:text-amber-700 font-semibold text-lg">Choisir une image</span>
                      <input type="file" className="hidden" accept="image/*" onChange={handleImageSelect} />
                    </label>
                    <p className="text-amber-700/70 mt-3 font-medium">PNG, JPG, JPEG jusqu'à 5MB</p>
                    <p className="text-amber-600/60 text-sm mt-1">Recommandé : 400x400px minimum</p>
                  </div>

                  <div className="flex gap-4 pt-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowImageModal(false)}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-2xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 font-semibold border border-gray-200"
                    >
                      Annuler
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleImageUpload}
                      disabled={!selectedImage}
                      className="flex-1 px-6 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl hover:from-amber-600 hover:to-orange-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 font-semibold shadow-lg"
                    >
                      {selectedImage ? "Enregistrer" : "Sélectionner une image"}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default Profile;
